import { useEffect, useMemo, useRef, useState } from 'react';
import { Info, MagnifyingGlass } from '@phosphor-icons/react';

/**
 * Text-entry primitives.
 *
 * Every field takes `note` rather than `error`. The wording is deliberate:
 * these are gentle prompts shown next to the field, not failure states.
 */

export function Field({ label, optional, hint, note, htmlFor, children }) {
  return (
    <div className="ob-field" data-missing={note ? 'true' : undefined}>
      {label && (
        <label className="ob-label" htmlFor={htmlFor}>
          {label}
          {optional && <span className="ob-optional">Optional</span>}
        </label>
      )}
      {hint && <span className="ob-hint">{hint}</span>}
      {children}
      {note && <Note>{note}</Note>}
    </div>
  );
}

export function Note({ children }) {
  return (
    <span className="ob-note" role="status">
      <Info size={14} weight="fill" />
      {children}
    </span>
  );
}

export function TextField({
  id, label, optional, hint, note, value = '', onChange,
  placeholder, type = 'text', inputMode, autoComplete, maxLength,
}) {
  return (
    <Field label={label} optional={optional} hint={hint} note={note} htmlFor={id}>
      <input
        id={id}
        className="ob-input"
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        maxLength={maxLength}
        placeholder={placeholder}
        value={value || ''}
        data-filled={!!value}
        aria-invalid={note ? 'true' : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function TextArea({
  id, label, optional, hint, note, value = '', onChange,
  placeholder, rows = 4, maxLength = 600, showCounter = true,
}) {
  const length = (value || '').length;
  const over = maxLength ? length > maxLength : false;

  return (
    <Field label={label} optional={optional} hint={hint} note={note} htmlFor={id}>
      <textarea
        id={id}
        className="ob-textarea"
        rows={rows}
        placeholder={placeholder}
        value={value || ''}
        data-filled={!!value}
        aria-invalid={note ? 'true' : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {showCounter && maxLength && (
        <span className="ob-counter" data-over={over}>
          {length} / {maxLength}
        </span>
      )}
    </Field>
  );
}

export function SelectField({
  id, label, optional, hint, note, value = '', onChange, options, placeholder = 'Choose one',
}) {
  return (
    <Field label={label} optional={optional} hint={hint} note={note} htmlFor={id}>
      <select
        id={id}
        className="ob-select"
        value={value || ''}
        data-filled={!!value}
        aria-invalid={note ? 'true' : undefined}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => {
          const key = typeof option === 'string' ? option : option.key;
          const text = typeof option === 'string' ? option : option.label;
          return <option key={key} value={key}>{text}</option>;
        })}
      </select>
    </Field>
  );
}

export function DateField({ id, label, optional, hint, note, value = '', onChange, min, max }) {
  return (
    <Field label={label} optional={optional} hint={hint} note={note} htmlFor={id}>
      <input
        id={id}
        className="ob-input"
        type="date"
        value={value || ''}
        min={min}
        max={max}
        data-filled={!!value}
        aria-invalid={note ? 'true' : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function Checkbox({ id, label, hint, checked, onChange }) {
  return (
    <button
      type="button"
      id={id}
      className="ob-choice"
      data-selected={!!checked}
      aria-pressed={!!checked}
      onClick={() => onChange(!checked)}
    >
      <span className="ob-choice-tick" data-shape="square" aria-hidden="true">
        {checked && (
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
            <path d="M2 6.2L4.8 9L10 3.4" stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <span className="ob-choice-text">
        <span className="ob-choice-title">{label}</span>
        {hint && <span className="ob-choice-hint">{hint}</span>}
      </span>
    </button>
  );
}

/**
 * Type-ahead over a fixed list that still accepts anything typed. Used for
 * city and nationality, where the list is a convenience rather than a limit.
 */
export function SearchSelect({
  id, label, optional, hint, note, value = '', onChange, options, placeholder,
}) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, []);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options.slice(0, 40);
    return options.filter((o) => o.toLowerCase().includes(needle)).slice(0, 40);
  }, [query, options]);

  const choose = (option) => {
    onChange(option);
    setQuery(option);
    setOpen(false);
  };

  return (
    <Field label={label} optional={optional} hint={hint} note={note} htmlFor={id}>
      <div className="ob-picker" ref={wrapRef}>
        <input
          id={id}
          className="ob-input"
          value={query}
          placeholder={placeholder}
          autoComplete="off"
          data-filled={!!value}
          aria-invalid={note ? 'true' : undefined}
          aria-expanded={open}
          role="combobox"
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
            setOpen(true);
          }}
        />
        {open && matches.length > 0 && (
          <div className="ob-picker-results" role="listbox">
            {matches.map((option) => (
              <button
                key={option}
                type="button"
                className="ob-picker-option"
                role="option"
                aria-selected={option === value}
                onClick={() => choose(option)}
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>
    </Field>
  );
}

export function SearchInput({ value, onChange, placeholder }) {
  return (
    <div style={{ position: 'relative' }}>
      <MagnifyingGlass
        size={17}
        style={{
          position: 'absolute', left: 14, top: '50%',
          transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none',
        }}
      />
      <input
        className="ob-input"
        style={{ paddingLeft: 40 }}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
